// @ts-check

import { Close, Fullscreen } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

/**
 * @typedef {import('@mui/material/Dialog').DialogProps} DialogProps
 */

/**
 * @typedef {Omit<DialogProps, 'title'> & {
 *   onClose: () => void,
 *   title: React.ReactNode,
 *   titleIcon?: React.ReactNode,
 *   contentText?: React.ReactNode,
 *   isLoading?: React.ReactNode,
 *   actions?: React.ReactNode,
 *   disableClose?: boolean,
 * }} MBModalProps
 */

/**
 * MBModal is a themed wrapper around MUI Dialog with fullscreen support and enhanced layout.
 *
 * @param {MBModalProps} props - Props for MBModal component.
 * @returns {JSX.Element}
 */
const MBModal = ({
  onClose,
  title,
  titleIcon,
  children,
  actions,
  contentText,
  isLoading,
  disableClose = false,
  ...others
}) => {
  const [fullScreen, setFullScreen] = useState(false);

  const toggleFullScreen = () => {
    setFullScreen((prev) => !prev);
  };

  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 1,
          boxShadow: 0,
        },
      }}
      fullScreen={fullScreen}
      closeAfterTransition={false}
      fullWidth
      onClose={disableClose ? undefined : onClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      {...others}
    >
      <DialogTitle id='alert-dialog-title' sx={{ maxWidth: '90%' }}>
        <Stack direction='row' alignItems='center' spacing={1}>
          {titleIcon}
          <Typography variant='h4'>{title}</Typography>
        </Stack>
      </DialogTitle>

      <Stack
        direction='row'
        alignItems='center'
        sx={{ position: 'absolute', right: '14px', top: '14px' }}
      >
        <Tooltip title={fullScreen ? 'Exit full screen' : 'Full screen'}>
          <IconButton
            color={fullScreen ? 'primary' : 'inherit'}
            size='small'
            aria-label='fullscreen'
            onClick={toggleFullScreen}
          >
            <Fullscreen />
          </IconButton>
        </Tooltip>
        <IconButton
          size='small'
          aria-label='close'
          onClick={onClose}
          disabled={disableClose}
        >
          <Close />
        </IconButton>
      </Stack>

      <DialogContent>
        <Box minHeight={'10px'}>
          {isLoading && (
            <LinearProgress classes={{ bar: 'mb-modal-loading-bar' }} />
          )}
        </Box>
        {contentText && <DialogContentText>{contentText}</DialogContentText>}
        {children}
      </DialogContent>

      {actions && <DialogActions sx={{ paddingX: 3 }}>{actions}</DialogActions>}
    </Dialog>
  );
};

export default MBModal;
